// src/pages/customers/[id]/tabs/orders.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getOrdersByCustomerId } from "../../customerService";
import API_BASE_URL from "../../../../utils/config";
import axios from "axios";

export default function OrdersTab() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingInvoice, setLoadingInvoice] = useState(null);

  const money = (v) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(Number(v || 0));

  useEffect(() => {
    (async () => {
      try {
        const data = await getOrdersByCustomerId(id);
        setOrders(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return <div className="p-6 text-gray-600">Loading orders...</div>;
  if (!orders.length) return <div className="p-6 text-gray-600">No orders found</div>;

  // Generate Invoice
  const handleGenerateInvoice = async (orderId) => {
    setLoadingInvoice(orderId);
    const token = localStorage.getItem("token");
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/invoices/generate/${orderId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Invoice generated successfully: #" + res.data.invoiceId);
      const updated = await getOrdersByCustomerId(id);
      setOrders(updated);
    } catch (err) {
      console.error("Invoice generation failed", err?.response?.data || err);
      alert("Failed to generate invoice");
    } finally {
      setLoadingInvoice(null);
    }
  };

  // Modify order
  const handleModifyOrder = (orderId) => {
    navigate(`/orders/${orderId}/modify`);
  };

  // Status change
  const handleStatusChange = async (orderId, newStatus) => {
    const token = localStorage.getItem("token");
    try {
      await axios.patch(
        `${API_BASE_URL}/api/orders/${orderId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const updated = await getOrdersByCustomerId(id);
      setOrders(updated);
    } catch (err) {
      console.error(err);
      alert("Failed to update status");
    }
  };

  const StatusBadge = ({ invoiced, status }) => {
    if (!invoiced) {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-700">
          Not invoiced
        </span>
      );
    }
    const tone =
      status === "delivered"
        ? "bg-emerald-100 text-emerald-700"
        : status === "shipped"
        ? "bg-sky-100 text-sky-700"
        : "bg-amber-100 text-amber-700"; // packed
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs ${tone}`}>
        {status || "packed"}
      </span>
    );
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex justify-end mb-1">
        <button
          onClick={() => navigate(`/orders/new?customerId=${id}`)}
          className="hidden sm:inline-flex px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
        >
          Add New Order
        </button>
      </div>

      <h2 className="text-2xl font-bold text-blue-900">Customer Orders</h2>

      {orders.map((order) => (
        <div
          key={order.id}
          className="bg-white shadow-sm rounded-2xl border border-slate-200 p-4"
        >
          {/* Header row */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-lg font-semibold text-blue-800">
                Order #{order.id}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                <StatusBadge
                  invoiced={!!order.invoice_generated}
                  status={order.status}
                />
                <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                  {new Date(order.created_at).toLocaleDateString()}
                </span>
                <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">
                  Doctor: {order.doctor_name || "N/A"}
                </span>
              </div>
            </div>

            {/* Actions / Status */}
            <div className="flex items-center gap-2">
              {!order.invoice_generated ? (
                <>
                  <button
                    onClick={() => handleGenerateInvoice(order.id)}
                    disabled={loadingInvoice === order.id}
                    className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loadingInvoice === order.id
                      ? "Generating..."
                      : "Generate Invoice"}
                  </button>
                  <button
                    onClick={() => handleModifyOrder(order.id)}
                    className="px-3 py-2 rounded-lg bg-amber-500 text-white text-sm hover:bg-amber-600"
                  >
                    Modify
                  </button>
                </>
              ) : (
                <select
                  value={order.status || "packed"}
                  onChange={(e) => handleStatusChange(order.id, e.target.value)}
                  className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"
                >
                  <option value="packed">Packed</option>
                  <option value="shipped">Shipped</option>
                  <option value="delivered">Delivered</option>
                </select>
              )}
            </div>
          </div>

          {/* Totals block */}
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 bg-gray-50 border border-slate-200 rounded-xl p-3">
            <div>
              <div className="text-xs text-slate-500">Subtotal</div>
              <div className="font-medium">{money(order.total_without_gst)}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Total GST</div>
              <div className="font-medium">{money(order.total_gst)}</div>
            </div>
            <div className="col-span-2 sm:col-span-1">
              <div className="text-xs text-slate-500">Grand Total</div>
              <div className="font-semibold text-slate-900">
                {money(order.total_amount)}
              </div>
            </div>
          </div>

          {/* ===== MOBILE: item list (cards/rows) ===== */}
          {order.items && order.items.length > 0 && (
            <div className="sm:hidden mt-4">
              <div className="text-sm font-semibold text-slate-700 mb-2">
                Items
              </div>
              <div className="rounded-xl border border-slate-200 divide-y">
                {order.items.map((item, idx) => (
                  <div key={item.id || idx} className="p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium text-slate-900 truncate">
                          {idx + 1}. {item.product_name}
                        </div>
                        <div className="mt-1 grid grid-cols-2 gap-2 text-xs text-slate-600">
                          <div>Qty: {item.quantity}</div>
                          <div className="text-right">
                            Rate: {money(item.rate || 0)}
                          </div>
                          <div>GST: {item.gst_percent || 0}%</div>
                          <div className="text-right">
                            GST Amt: {money(item.gst_amount || 0)}
                          </div>
                        </div>
                      </div>
                      <div className="text-right font-semibold">
                        {money(item.total)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ===== DESKTOP/TABLET: items table ===== */}
          {order.items && order.items.length > 0 && (
            <div className="hidden sm:block mt-4 overflow-x-auto">
              <table className="min-w-full text-sm border border-slate-200 rounded-xl overflow-hidden">
                <thead>
                  <tr className="bg-blue-600 text-white">
                    <th className="px-3 py-2 text-left">#</th>
                    <th className="px-3 py-2 text-left">Product</th>
                    <th className="px-3 py-2 text-center">Qty</th>
                    <th className="px-3 py-2 text-right">Price</th>
                    <th className="px-3 py-2 text-right">GST %</th>
                    <th className="px-3 py-2 text-right">GST Amt</th>
                    <th className="px-3 py-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="[&>tr:nth-child(even)]:bg-slate-50">
                  {order.items.map((item, idx) => (
                    <tr key={item.id || idx} className="text-slate-800">
                      <td className="px-3 py-2">{idx + 1}</td>
                      <td className="px-3 py-2">{item.product_name}</td>
                      <td className="px-3 py-2 text-center">{item.quantity}</td>
                      <td className="px-3 py-2 text-right">
                        {money(item.rate || 0)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {(item.gst_percent || 0) + "%"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {money(item.gst_amount || 0)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {money(item.total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* MOBILE: floating Add button */}
          <div className="sm:hidden mt-4">
            <button
              onClick={() => navigate(`/orders/new?customerId=${id}`)}
              className="w-full rounded-lg bg-green-600 text-white py-2 font-medium"
            >
              Add New Order
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
